
import { Code2, Wrench, Cog, Puzzle, Globe, Settings, Smartphone, Building2, Link, Cloud, Database, Network, Brain } from 'lucide-react';
import type { Product } from './types';

export const customProducts: Product[] = [
  {
    id: 'custom-webapp-development',
    title: 'Custom Web Application Development',
    subtitle: 'Tailored web solutions',
    description: 'Bespoke web application development with modern frameworks, scalable architecture, and custom functionality tailored to your business requirements.',
    icon: Code2,
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Custom user interface design',
      'Scalable backend architecture',
      'API development & integration',
      'Database design & optimisation',
      'Security implementation',
      'Performance optimisation',
      'Testing & quality assurance',
      'Deployment & maintenance'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#custom-development', '#web-applications', '#bespoke-software', '#full-stack', '#scalable-solutions']
  },
  {
    id: 'mobile-app-development',
    title: 'Mobile Application Development',
    subtitle: 'Native & cross-platform apps',
    description: 'Professional mobile application development for iOS and Android platforms with native performance and cross-platform compatibility.',
    icon: Smartphone,
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Native iOS & Android development',
      'Cross-platform solutions',
      'User experience design',
      'Performance optimisation',
      'App store deployment',
      'Push notifications',
      'Offline functionality',
      'Analytics integration'
    ],
    pricing: { setup: 76800, monthly: 3533 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#mobile-development', '#ios-apps', '#android-apps', '#cross-platform', '#native-apps']
  },
  {
    id: 'enterprise-software-solution',
    title: 'Enterprise Software Solutions',
    subtitle: 'Large-scale business systems',
    description: 'Comprehensive enterprise software solutions designed for large organisations with complex workflows, integration requirements, and scalability needs for UK market.',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Enterprise architecture design',
      'Legacy system integration',
      'Workflow automation',
      'Multi-tenant architecture',
      'Advanced security protocols',
      'Scalability planning',
      'Compliance frameworks',
      'Enterprise support'
    ],
    pricing: { setup: 192000, monthly: 8832 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#enterprise-software', '#large-scale-systems', '#workflow-automation', '#legacy-integration', '#compliance']
  },
  {
    id: 'api-integration-services',
    title: 'API Integration & Development Services',
    subtitle: 'Seamless system connectivity',
    description: 'Professional API development and integration services to connect disparate systems, automate workflows, and enable seamless data exchange.',
    icon: Link,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'RESTful API development',
      'GraphQL implementation',
      'Third-party integrations',
      'Webhook management',
      'API documentation',
      'Rate limiting & security',
      'Monitoring & analytics',
      'Version management'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#api-development', '#system-integration', '#workflow-automation', '#data-exchange', '#connectivity']
  },
  {
    id: 'cloud-migration-services',
    title: 'Cloud Migration & Modernization',
    subtitle: 'Digital transformation expertise',
    description: 'Complete cloud migration and modernization services including architecture assessment, migration strategy, and ongoing optimization.',
    icon: Cloud,
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Cloud architecture assessment',
      'Migration strategy planning',
      'Data migration services',
      'Application modernization',
      'Security implementation',
      'Performance optimisation',
      'Cost optimisation',
      'Ongoing support'
    ],
    pricing: { setup: 115200, monthly: 5299 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#cloud-migration', '#digital-transformation', '#modernization', '#aws', '#azure']
  },
  {
    id: 'database-design-optimization',
    title: 'Database Design & Optimization',
    subtitle: 'High-performance data solutions',
    description: 'Expert database design, optimization, and management services for high-performance applications with complex data requirements.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Database architecture design',
      'Performance optimisation',
      'Query optimisation',
      'Indexing strategies',
      'Backup & recovery planning',
      'Security implementation',
      'Scalability planning',
      'Migration services'
    ],
    pricing: { setup: 56320, monthly: 2591 },
    color: 'from-teal-500 to-green-600',
    businessTypes: ['#database-design', '#performance-optimization', '#data-architecture', '#scalability', '#database-management']
  },
  {
    id: 'devops-automation-services',
    title: 'DevOps & CI/CD Automation',
    subtitle: 'Streamlined development workflows',
    description: 'Comprehensive DevOps implementation with continuous integration, automated deployment pipelines, and infrastructure as code.',
    icon: Settings,
    image: 'https://images.unsplash.com/photo-1551808525-51a94da548ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'CI/CD pipeline setup',
      'Infrastructure as code',
      'Automated testing',
      'Deployment automation',
      'Monitoring & alerting',
      'Container orchestration',
      'Security scanning',
      'Performance monitoring'
    ],
    pricing: { setup: 89600, monthly: 4122 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#devops', '#cicd', '#automation', '#infrastructure', '#deployment']
  },
  {
    id: 'enterprise-microservices-architecture',
    title: 'Enterprise Microservices Architecture Development',
    subtitle: 'Cloud-native application design',
    description: 'Advanced microservices architecture development with cloud-native design, Kubernetes orchestration, and comprehensive DevSecOps implementation for scalable enterprise applications.',
    icon: Network,
    image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Microservices architecture design',
      'Kubernetes orchestration',
      'Service mesh implementation',
      'API gateway configuration',
      'DevSecOps pipeline',
      'Container security',
      'Monitoring and observability',
      'Auto-scaling capabilities'
    ],
    pricing: { setup: 128000, monthly: 5888 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#microservices', '#kubernetes', '#cloud-native', '#devsecops', '#enterprise-architecture']
  },
  {
    id: 'ai-ml-custom-solutions',
    title: 'AI/ML Custom Solution Development',
    subtitle: 'Custom AI model creation',
    description: 'Comprehensive AI and machine learning solution development with custom model creation, neural network implementation, and advanced computer vision and NLP capabilities.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Custom AI model development',
      'Neural network implementation',
      'Computer vision solutions',
      'Natural language processing',
      'Deep learning algorithms',
      'Model training and optimisation',
      'AI pipeline automation',
      'Explainable AI implementation'
    ],
    pricing: { setup: 115200, monthly: 5299 },
    color: 'from-pink-500 to-rose-600',
    businessTypes: ['#artificial-intelligence', '#machine-learning', '#custom-ai', '#neural-networks', '#computer-vision']
  },
  {
    id: 'blockchain-web3-development',
    title: 'Blockchain & Web3 Development Suite',
    subtitle: 'Smart contract development',
    description: 'Complete blockchain and Web3 development services including smart contract development, DeFi platform creation, and NFT marketplace development for next-generation applications.',
    icon: Link,
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Custom Development',
    status: 'Available',
    features: [
      'Smart contract development',
      'DeFi platform creation',
      'NFT marketplace development',
      'Blockchain integration',
      'Cryptocurrency wallets',
      'Decentralized applications',
      'Token economics design',
      'Security auditing'
    ],
    pricing: { setup: 102400, monthly: 4710 },
    color: 'from-amber-500 to-orange-600',
    businessTypes: ['#blockchain', '#web3', '#smart-contracts', '#defi', '#nft-marketplace']
  }
];
